import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  AlertTriangle,
  Anchor,
  Bell,
  BellOff,
  CheckCheck,
  Droplets,
  Gauge,
  LogIn,
  LogOut,
  Power,
  RotateCw,
  Search,
  Settings,
  Ship,
  Trash2,
  WifiOff,
  Wrench,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useEventsStore } from '@/stores/events';
import { useFleetStore } from '@/stores/fleet';
import { reportsApi } from '@/api/endpoints/reports';
import { translateEvent } from '@/lib/traccarLabels';
import { cn, formatDateTime, formatRelativeTime } from '@/lib/utils';
import { eventRisk, RISK_META, type Risk } from '@/lib/eventRisk';
import type { FeedEvent } from '@/stores/events';

type Filter = 'all' | 'unread';
const riskOf = eventRisk;

export function NotificationsPage() {
  const items = useEventsStore((s) => s.items);
  const markRead = useEventsStore((s) => s.markRead);
  const markAllRead = useEventsStore((s) => s.markAllRead);
  const clear = useEventsStore((s) => s.clear);
  const devices = useFleetStore((s) => s.devices);

  const [filter, setFilter] = useState<Filter>('all');
  const [riskFilter, setRiskFilter] = useState<'all' | Risk>('all');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const riskCounts = useMemo(() => {
    const c = { critico: 0, atencao: 0, normal: 0 } as Record<Risk, number>;
    for (const e of items) c[riskOf(e.type)] += 1;
    return c;
  }, [items]);

  const types = useMemo(() => {
    const seen = new Set<string>();
    for (const e of items) seen.add(e.type);
    return Array.from(seen).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((e) => {
      // Isolamento por dispositivo (defesa em profundidade): só eventos de devices na
      // frota atual do usuário; eventos de sistema (sem deviceId) passam.
      if (e.deviceId != null && !devices.has(e.deviceId)) return false;
      if (filter === 'unread' && e.read) return false;
      if (riskFilter !== 'all' && riskOf(e.type) !== riskFilter) return false;
      if (typeFilter !== 'all' && e.type !== typeFilter) return false;
      if (q) {
        const device = e.deviceId != null ? devices.get(e.deviceId) : undefined;
        const haystack = `${translateEvent(e.type)} ${device?.name ?? ''} ${e.type}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [items, filter, riskFilter, typeFilter, search, devices]);

  const unreadCount = useMemo(() => items.filter((e) => !e.read).length, [items]);

  const reloadMutation = useMutation({
    mutationFn: async () => {
      const devices = useFleetStore.getState().devices;
      const ids = Array.from(devices.keys());
      if (ids.length === 0) return 0;
      const to = new Date();
      const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
      const events = await reportsApi.events({
        deviceId: ids,
        from: from.toISOString(),
        to: to.toISOString(),
      });
      // O store já tem dedup por id+serverTime
      const before = useEventsStore.getState().items.length;
      useEventsStore.getState().push(events);
      const after = useEventsStore.getState().items.length;
      return after - before;
    },
  });

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Alertas náuticos</h1>
          <p className="text-sm text-muted-foreground">
            Eventos das embarcações chegam aqui em tempo real conforme as regras configuradas.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => reloadMutation.mutate()}
            disabled={reloadMutation.isPending}
            className="gap-1.5"
          >
            <RotateCw className={cn('h-4 w-4', reloadMutation.isPending && 'animate-spin')} />
            {reloadMutation.isPending ? 'Carregando…' : 'Histórico 7 dias'}
          </Button>
          <Button
            variant="outline"
            onClick={() => markAllRead()}
            disabled={unreadCount === 0}
            className="gap-1.5"
          >
            <CheckCheck className="h-4 w-4" />
            Marcar todas
          </Button>
          <Link
            to="/notifications/rules"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-4 text-sm font-semibold hover:bg-accent"
          >
            <Settings className="h-4 w-4" />
            Regras
          </Link>
        </div>
      </div>

      {reloadMutation.isSuccess && (
        <p className="rounded-md bg-status-running/10 px-3 py-2 text-xs font-medium text-status-running">
          {reloadMutation.data === 0
            ? 'Histórico já estava atualizado.'
            : `${reloadMutation.data} novo(s) evento(s) adicionado(s) ao feed.`}
        </p>
      )}
      {reloadMutation.error && (
        <p className="rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
          Erro ao carregar histórico de 7 dias.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setFilter('all')}
          className={cn(
            'rounded-full px-3 py-1.5 text-xs font-semibold transition-all',
            filter === 'all'
              ? 'bg-card text-foreground shadow-sm ring-1 ring-border'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Todas · {items.length}
        </button>
        <button
          type="button"
          onClick={() => setFilter('unread')}
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-all',
            filter === 'unread'
              ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Não-lidas
          {unreadCount > 0 && (
            <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
              {unreadCount}
            </span>
          )}
        </button>

        <span className="mx-1 h-4 w-px bg-border" />
        {(['critico', 'atencao', 'normal'] as Risk[]).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRiskFilter(riskFilter === r ? 'all' : r)}
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold ring-1 transition-all',
              riskFilter === r ? RISK_META[r].chip : 'text-muted-foreground ring-transparent hover:text-foreground',
            )}
          >
            {RISK_META[r].label} {riskCounts[r]}
          </button>
        ))}

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="ml-auto h-8 rounded-md border border-input bg-background px-2 text-xs"
        >
          <option value="all">Todos os tipos</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {translateEvent(t)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Apagar todos os eventos do feed local?')) clear();
          }}
          disabled={items.length === 0}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-accent disabled:opacity-50"
          title="Limpar feed local"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por embarcação ou tipo de evento"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            {items.length === 0 ? (
              <BellOff className="h-10 w-10 text-muted-foreground/50" />
            ) : (
              <Bell className="h-10 w-10 text-muted-foreground/50" />
            )}
            <p className="text-sm text-muted-foreground">
              {items.length === 0
                ? 'Nenhuma notificação por enquanto. Configure regras e os eventos das embarcações aparecem aqui em tempo real.'
                : 'Nada corresponde aos filtros.'}
            </p>
            {items.length === 0 && (
              <Link
                to="/notifications/rules"
                className="text-xs font-semibold text-primary hover:underline"
              >
                Configurar regras
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <ul className="divide-y divide-border">
              {filtered.map((e) => (
                <EventRow
                  key={e.uid}
                  event={e}
                  onClick={() => {
                    if (!e.read) markRead([e.uid]);
                  }}
                />
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

type AlertTone = 'danger' | 'warn' | 'ok' | 'muted';

const TONE_BADGE: Record<AlertTone, string> = {
  danger: 'bg-status-blocked/15 text-status-blocked',
  warn: 'bg-status-ready/15 text-status-ready',
  ok: 'bg-status-running/15 text-status-running',
  muted: 'bg-secondary text-muted-foreground',
};

/** Ícone + severidade por tipo de evento (vocabulário náutico). */
function eventVisual(type: string): { icon: typeof Bell; tone: AlertTone } {
  switch (type) {
    case 'alarm':
      return { icon: Droplets, tone: 'danger' };
    case 'geofenceExit':
      return { icon: LogOut, tone: 'warn' };
    case 'geofenceEnter':
      return { icon: LogIn, tone: 'ok' };
    case 'deviceOverspeed':
      return { icon: Gauge, tone: 'warn' };
    case 'deviceOffline':
    case 'deviceUnknown':
    case 'deviceInactive':
      return { icon: WifiOff, tone: 'muted' };
    case 'ignitionOn':
      return { icon: Power, tone: 'ok' };
    case 'ignitionOff':
      return { icon: Power, tone: 'muted' };
    case 'deviceMoving':
      return { icon: Ship, tone: 'ok' };
    case 'parking':
    case 'deviceStopped':
      return { icon: Anchor, tone: 'muted' };
    case 'maintenance':
      return { icon: Wrench, tone: 'warn' };
    case 'deviceOnline':
      return { icon: Ship, tone: 'ok' };
    default:
      return { icon: AlertTriangle, tone: 'warn' };
  }
}

function EventRow({ event, onClick }: { event: FeedEvent; onClick: () => void }) {
  const devices = useFleetStore((s) => s.devices);
  const device = event.deviceId != null ? devices.get(event.deviceId) : undefined;
  const label = translateEvent(event.type);
  const linkTo = device ? `/devices/${device.id}` : null;
  const { icon: EventIcon, tone } = eventVisual(event.type);

  const innerContent = (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 transition-colors hover:bg-accent/50',
        !event.read && 'bg-primary/5',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          TONE_BADGE[tone],
        )}
      >
        <EventIcon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-semibold">
            {label}
            {!event.read && (
              <span className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle" />
            )}
          </p>
          <span className="shrink-0 text-[11px] text-muted-foreground" title={formatDateTime(event.serverTime)}>
            {formatRelativeTime(event.serverTime)}
          </span>
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {device ? device.name : event.deviceId ? `Embarcação ${event.deviceId}` : 'Sistema'}
          {device?.model && ` · ${device.model}`}
        </p>
        <div className="mt-0.5 flex items-center gap-2">
          <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-bold', TONE_BADGE[tone])}>
            {RISK_META[riskOf(event.type)].label}
          </span>
          <span className="text-[10px] text-muted-foreground/70">{formatDateTime(event.serverTime)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <li>
      {linkTo ? (
        <Link to={linkTo} onClick={onClick} className="block">
          {innerContent}
        </Link>
      ) : (
        <button type="button" onClick={onClick} className="block w-full text-left">
          {innerContent}
        </button>
      )}
    </li>
  );
}
